import type Pubnub from 'pubnub';

export enum StoryCoEditingMessageType {
	join = 'story.join',
	leave = 'story.leave',
}

/** Ignore join signals older than this when deciding occupancy / kick-out. */
export const STORY_JOIN_MESSAGE_MAX_AGE_MS = 5 * 60 * 1000;

type PBMessage = Pubnub.FetchMessagesResponse['channels'][string][number];

export interface StoryJoinMessage extends PBMessage {
	message: {
		type: StoryCoEditingMessageType.join;
		email: string;
		name: string;
	};
}

export interface StoryJoinMessageEvent extends Pubnub.MessageEvent {
	message: {
		type: StoryCoEditingMessageType.join;
		email: string;
		name: string;
	};
}

export const pubnubTimetokenToMs = (timetoken: string | number): number => Math.floor(Number(timetoken) / 10000);

export const isJoinMessageRecent = (
	msg: { timetoken?: string | number },
	maxAgeMs = STORY_JOIN_MESSAGE_MAX_AGE_MS
): boolean => {
	if (msg.timetoken == null) {
		return false;
	}

	return Date.now() - pubnubTimetokenToMs(msg.timetoken) < maxAgeMs;
};

export function isJoinMessageEvent(msgEvent: Pubnub.MessageEvent): msgEvent is StoryJoinMessageEvent {
	return msgEvent?.message?.type === StoryCoEditingMessageType.join;
}

export function isOccupiedByJoinMessage(msg: PBMessage | undefined, uuid: string): msg is StoryJoinMessage {
	return msg !== undefined && msg?.message?.type === StoryCoEditingMessageType.join && msg.uuid !== uuid;
}

/**
 * True when another editor is likely still active: recent join, or their uuid still in presence.
 */
export function isStoryActivelyOccupiedByOther(
	lastMsg: PBMessage | undefined,
	occupants: { uuid: string }[] | undefined,
	myUuid: string
): boolean {
	if (!isOccupiedByJoinMessage(lastMsg, myUuid)) {
		return false;
	}

	if (isJoinMessageRecent(lastMsg)) {
		return true;
	}

	const otherEditorUuid = lastMsg.uuid;
	return occupants?.some(occupant => occupant.uuid === otherEditorUuid) ?? false;
}
